import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixEcomOrdersDraftV1DraftOrdersUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/ecom/v1/draft-orders',
        destPath: '/v1/draft-orders',
      },
      {
        srcPath: '/_api/v1/draft-orders',
        destPath: '/v1/draft-orders',
      },
      {
        srcPath: '/v1/draft-orders',
        destPath: '/v1/draft-orders',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/draft-orders',
        destPath: '/v1/draft-orders',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_draft-orders';

/**
 * Creates or gets a draft order.
 *
 * When passing the ID of an order that is already in draft, the existing draft order will be returned.
 * To complete a draft and update the order, call [Commit Draft Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/draft-orders/commit-draft-order).
 */
export function createDraftOrder(payload: object): RequestOptionsFactory<any> {
  function __createDraftOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.CreateDraftOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createDraftOrder;
}

/** Creates an empty draft order. */
export function createEmptyDraftOrder(
  payload: object
): RequestOptionsFactory<any> {
  function __createEmptyDraftOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.CreateEmptyDraftOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/create-empty-draft-order',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createEmptyDraftOrder;
}

/** Adds line items to a draft order. */
export function addLineItemsToDraftOrder(
  payload: object
): RequestOptionsFactory<any> {
  function __addLineItemsToDraftOrder({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'customLineItems.media.urlExpirationDate' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'customLineItems.media.focalPoint.x' },
          { path: 'customLineItems.media.focalPoint.y' },
          { path: 'customLineItems.physicalProperties.weight' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.AddLineItemsToDraftOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/add-line-items',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addLineItemsToDraftOrder;
}

/**
 * Updates a draft order's line items.
 *
 * Using this API, you can update a line item's quantity, price, and description.
 * > **Notes:**
 * > + Passing a quantity of `0` will remove the line item.
 * > + Price cannot be updated for line items with `paymentOption: MEMBERSHIP`.
 * > + Quantity cannot be decreased fulfilled line items. To get fulfillment information, pass the order ID to [List Fulfillments For Single Order](https://dev.wix.com/docs/rest/api-reference/wix-e-commerce/order-fulfillments/list-fulfillments-for-single-order).
 */
export function updateLineItems(payload: object): RequestOptionsFactory<any> {
  function __updateLineItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.UpdateLineItems',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/line-items',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateLineItems;
}

/**
 * Sets and enables or disables discounts on a draft order.
 *
 * Any discounts not passed will not change.
 */
export function setDiscounts(payload: object): RequestOptionsFactory<any> {
  function __setDiscounts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetDiscounts',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/set-discounts',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setDiscounts;
}

/** Adds merchant discounts to the order. */
export function createCustomDiscounts(
  payload: object
): RequestOptionsFactory<any> {
  function __createCustomDiscounts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.CreateCustomDiscounts',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/create-custom-discounts',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCustomDiscounts;
}

/** Remove custom discounts from the order. */
export function deleteCustomDiscounts(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCustomDiscounts({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.DeleteCustomDiscounts',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/delete-custom-discounts',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteCustomDiscounts;
}

/**
 * Set which additional fees existing on the draft order are to be opt-in.
 * All existing additional fees not provided on the call are opted-out.
 */
export function setAdditionalFees(payload: object): RequestOptionsFactory<any> {
  function __setAdditionalFees({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetAdditionalFees',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/set-additional-fees',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setAdditionalFees;
}

/**
 * Create custom additional fees to the order.
 * added additional fees are by default opted-out.
 * To opt-in them, please call SetAdditionalFees with the added additional fee ids.
 */
export function createCustomAdditionalFees(
  payload: object
): RequestOptionsFactory<any> {
  function __createCustomAdditionalFees({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.CreateCustomAdditionalFees',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/create-additional-fees',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCustomAdditionalFees;
}

/** Remove custom additional fees from the order. */
export function deleteCustomAdditionalFees(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCustomAdditionalFees({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.DeleteCustomAdditionalFees',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath:
          '/v1/draft-orders/{draftOrderId}/delete-custom-additional-fees',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __deleteCustomAdditionalFees;
}

/** Sets shipping info to a draft order. */
export function setShippingInfo(payload: object): RequestOptionsFactory<any> {
  function __setShippingInfo({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'shippingInfo.logistics.deliverByDate' },
          { path: 'shippingInfo.logistics.deliveryTimeSlot.from' },
          { path: 'shippingInfo.logistics.deliveryTimeSlot.to' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'shippingInfo.logistics.shippingDestination.address.geocode.latitude',
          },
          {
            path: 'shippingInfo.logistics.shippingDestination.address.geocode.longitude',
          },
          {
            path: 'shippingInfo.logistics.pickupDetails.address.geocode.latitude',
          },
          {
            path: 'shippingInfo.logistics.pickupDetails.address.geocode.longitude',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'PUT' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetShippingInfo',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/set-shipping-info',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setShippingInfo;
}

/** Sets buyer info to a draft order. */
export function setBuyerInfo(payload: object): RequestOptionsFactory<any> {
  function __setBuyerInfo({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'PUT' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetBuyerInfo',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/set-buyer-info',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setBuyerInfo;
}

/** Sets recipient info to a draft order. */
export function setRecipientInfo(payload: object): RequestOptionsFactory<any> {
  function __setRecipientInfo({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'recipientInfo.address.geocode.latitude' },
          { path: 'recipientInfo.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'PUT' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetRecipientInfo',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/set-recipient-info',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setRecipientInfo;
}

/** Sets billing info to a draft order. */
export function setBillingInfo(payload: object): RequestOptionsFactory<any> {
  function __setBillingInfo({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'billingInfo.address.geocode.latitude' },
          { path: 'billingInfo.address.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'PUT' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetBillingInfo',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/set-billing-info',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setBillingInfo;
}

/**
 * Get an order.
 * When order is in draft status, it's re-estimated and return with latest tax and totals
 * Automatic discounts and automatic additional fees will reflect the latest prices and eligibility.
 * Calculation will be based on current draft order content - items, shipping info and eligible discounts and additional fees.
 * When order is in committed status, it's returned with it's calculation at the moment it was committed.
 */
export function getDraftOrder(payload: object): RequestOptionsFactory<any> {
  function __getDraftOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'GET' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.GetDraftOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
            protoPath: '/v1/draft-orders/{draftOrderId}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getDraftOrder;
}

/** Checks whether a draft can be created for this order */
export function getOrderDraftabilityStatus(
  payload: object
): RequestOptionsFactory<any> {
  function __getOrderDraftabilityStatus({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'GET' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.GetOrderDraftabilityStatus',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{orderId}/draftability',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getOrderDraftabilityStatus;
}

/**
 * Commit latest changes to the baseline order and control what side-effects will be applied.
 * Commit cannot be undone or reverted.
 * Order-edit become closed for editing, however it's kept for reference and can be retrieved.
 */
export function commitDraftOrder(payload: object): RequestOptionsFactory<any> {
  function __commitDraftOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.CommitDraftOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/commit',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'committedDraftOrder.createdDate' },
              { path: 'committedDraftOrder.updatedDate' },
              {
                path: 'committedDraftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'committedDraftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'committedDraftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'orderAfterCommit.createdDate' },
              { path: 'orderAfterCommit.updatedDate' },
              { path: 'orderAfterCommit.purchasedDate' },
              { path: 'orderAfterCommit.lineItems.image.urlExpirationDate' },
              { path: 'orderAfterCommit.lineItems.digitalFile.expirationDate' },
              {
                path: 'orderAfterCommit.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'orderAfterCommit.shippingInfo.logistics.deliverByDate' },
              {
                path: 'orderAfterCommit.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'orderAfterCommit.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'orderAfterCommit.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'committedDraftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'committedDraftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              { path: 'orderAfterCommit.lineItems.image.focalPoint.x' },
              { path: 'orderAfterCommit.lineItems.image.focalPoint.y' },
              {
                path: 'committedDraftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              { path: 'orderAfterCommit.lineItems.physicalProperties.weight' },
              { path: 'orderAfterCommit.billingInfo.address.geocode.latitude' },
              {
                path: 'orderAfterCommit.billingInfo.address.geocode.longitude',
              },
              {
                path: 'orderAfterCommit.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'orderAfterCommit.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'orderAfterCommit.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'orderAfterCommit.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'orderAfterCommit.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'orderAfterCommit.recipientInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __commitDraftOrder;
}

/**
 * Create new order and control what side-effects will be applied
 * Commit cannot be undone or reverted.
 * Order-edit become closed for editing, however it's kept for reference and can be retrieved.
 */
export function createOrderFromDraft(
  payload: object
): RequestOptionsFactory<any> {
  function __createOrderFromDraft({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn:
        'com.wix.ecom.orders.draft.v1.DraftOrders.CreateOrderFromDraft',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}/create-order',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'committedDraftOrder.createdDate' },
              { path: 'committedDraftOrder.updatedDate' },
              {
                path: 'committedDraftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'committedDraftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'committedDraftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'createdOrder.createdDate' },
              { path: 'createdOrder.updatedDate' },
              { path: 'createdOrder.purchasedDate' },
              { path: 'createdOrder.lineItems.image.urlExpirationDate' },
              { path: 'createdOrder.lineItems.digitalFile.expirationDate' },
              {
                path: 'createdOrder.lineItems.subscriptionInfo.subscriptionSettings.startDate',
              },
              { path: 'createdOrder.shippingInfo.logistics.deliverByDate' },
              {
                path: 'createdOrder.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'createdOrder.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              { path: 'createdOrder.activities.createdDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'committedDraftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'committedDraftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              { path: 'createdOrder.lineItems.image.focalPoint.x' },
              { path: 'createdOrder.lineItems.image.focalPoint.y' },
              {
                path: 'committedDraftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'committedDraftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              { path: 'createdOrder.lineItems.physicalProperties.weight' },
              { path: 'createdOrder.billingInfo.address.geocode.latitude' },
              { path: 'createdOrder.billingInfo.address.geocode.longitude' },
              {
                path: 'createdOrder.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'createdOrder.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'createdOrder.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'createdOrder.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'createdOrder.recipientInfo.address.geocode.latitude' },
              { path: 'createdOrder.recipientInfo.address.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createOrderFromDraft;
}

/**
 * Delete order-edit entity with all pending changes.
 * Order-edit in status 'COMMITTED' cannot be deleted.
 */
export function deleteDraftOrder(payload: object): RequestOptionsFactory<any> {
  function __deleteDraftOrder({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'DELETE' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.DeleteDraftOrder',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/{draftOrderId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteDraftOrder;
}

/**
 * Retrieves a list of draft orders given the provided paging, filtering, and sorting. Up to 100 draft orders can be returned per request.
 *
 * Refer to the table below for supported fields, filters, and sorting:
 * | **Property**   | **Supported Filters & Sorting**                                                 | **Sortable** |
 * |----------------|---------------------------------------------------------------------------------|--------------|
 * | `_createdDate` | `eq()`, `ne()`, `exists()`, `in()`, `hasSome()`, `lt()`, `le()`, `gt()`, `ge()` | Sortable     |
 * | `_updatedDate` | `eq()`, `ne()`, `exists()`, `in()`, `hasSome()`, `lt()`, `le()`, `gt()`, `ge()` | Sortable     |
 * | `status`       | `eq()`, `ne()`, `exists()`, `in()`, `hasSome()`                                 | Sortable     |
 * | `orderId`      | `eq()`, `ne()`, `exists()`, `in()`, `hasSome()`, `startsWith()`                 |              |
 * To learn how to query draft orders, see [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
 */
export function queryDraftOrders(payload: object): RequestOptionsFactory<any> {
  function __queryDraftOrders({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'POST' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.QueryDraftOrders',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'draftOrders.createdDate' },
              { path: 'draftOrders.updatedDate' },
              {
                path: 'draftOrders.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'draftOrders.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'draftOrders.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'draftOrders.lineItems.lineItem.image.focalPoint.x' },
              { path: 'draftOrders.lineItems.lineItem.image.focalPoint.y' },
              {
                path: 'draftOrders.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'draftOrders.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'draftOrders.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              { path: 'draftOrders.billingInfo.address.geocode.latitude' },
              { path: 'draftOrders.billingInfo.address.geocode.longitude' },
              {
                path: 'draftOrders.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'draftOrders.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'draftOrders.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'draftOrders.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'draftOrders.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'draftOrders.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'draftOrders.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'draftOrders.billingDetails.billingInfo.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryDraftOrders;
}

/** Sets tax exemption to a draft order. */
export function setTaxExemption(payload: object): RequestOptionsFactory<any> {
  function __setTaxExemption({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'PUT' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetTaxExemption',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/set-tax-exemption',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setTaxExemption;
}

/** Sets business location to a draft order. */
export function setBusinessLocation(
  payload: object
): RequestOptionsFactory<any> {
  function __setBusinessLocation({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.draft_order',
      method: 'PUT' as any,
      methodFqn: 'com.wix.ecom.orders.draft.v1.DraftOrders.SetBusinessLocation',
      packageName: PACKAGE_NAME,
      url: resolveComWixEcomOrdersDraftV1DraftOrdersUrl({
        protoPath: '/v1/draft-orders/set-business-location',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'calculatedDraftOrder.draftOrder.createdDate' },
              { path: 'calculatedDraftOrder.draftOrder.updatedDate' },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.urlExpirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.digitalFile.expirationDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.subscriptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliverByDate',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.deliveryTimeSlot.to',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.from',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.deliveryTimeSlot.to',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.x',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.image.focalPoint.y',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.lineItems.lineItem.physicalProperties.weight',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.changedDetails.shippingInfoBeforeChange.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.shippingDestination.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.shippingInfo.shippingInfo.logistics.pickupDetails.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.changedDetails.recipientInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.recipientInfo.recipientInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.changedDetails.billingInfoBeforeChange.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.draftOrder.billingDetails.billingInfo.address.geocode.longitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.latitude',
              },
              {
                path: 'calculatedDraftOrder.shippingOptions.logistics.pickupDetails.address.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setBusinessLocation;
}
